import JSZip from "jszip";

export interface ArchiveFile {
  path: string;
  content: string;
  type: string;
  fileName: string;
}

export interface ProcessedArchive {
  repoData: Record<string, ArchiveFile>;
  totalFiles: number;
}

// Text file extensions for filtering (same as backend)
const textExtensions = [
  "txt",
  "md",
  "markdown",
  "js",
  "jsx",
  "ts",
  "tsx",
  "json",
  "html",
  "htm",
  "css",
  "scss",
  "less",
  "py",
  "rb",
  "java",
  "c",
  "cpp",
  "h",
  "hpp",
  "cs",
  "go",
  "php",
  "pl",
  "sh",
  "bash",
  "xml",
  "yaml",
  "yml",
  "ini",
  "cfg",
  "conf",
  "toml",
  "sql",
  "graphql",
  "swift",
  "kt",
  "rs",
  "dart",
  "lua",
  "r",
  "vue",
  "svelte",
  "mjs",
  "cjs",
  "ejs",
  "pug",
  "jade",
  "haml",
  "hbs",
  "handlebars",
  "m",
  "mm",
  "f",
  "f90",
  "ex",
  "exs",
  "erl",
  "hrl",
  "clj",
  "cljs",
  "scala",
  "sc",
  "groovy",
  "gradle",
  "tf",
  "tfvars",
  "proto",
  "sol",
  "asm",
  "s",
  "d",
  "elm",
  "jl",
  "zig",
  "v",
  "nim",
  "cr",
  "ml",
  "mli",
  "fs",
  "fsi",
  "fsx",
  "fsscript",
  "tcl",
  "vb",
  "ps1",
  "psm1",
  "psd1",
  "dockerfile",
  "makefile",
  "cmake",
  "rake",
  "gemfile",
  "podfile",
];
const TEXT_EXTENSIONS_SET = new Set(textExtensions);

const isTextFile = (filename: string): boolean => {
  const ext = filename.split(".").pop()?.toLowerCase();
  return ext ? TEXT_EXTENSIONS_SET.has(ext) : true;
};

const isIgnoreFile = (filename: string): boolean => {
  // Common directories to ignore (same as backend)
  if (
    filename.includes("node_modules") ||
    filename.includes("/dist/") ||
    filename.includes("/build/") ||
    filename.includes("/public/assets/") ||
    filename.includes("package-lock.json") ||
    filename.includes("package.json") ||
    filename.includes("/.git/") ||
    filename.includes("/.github/") ||
    filename.includes("/coverage/") ||
    filename.includes("/.nyc_output/") ||
    filename.includes("/target/") ||
    filename.includes("/bin/") ||
    filename.includes("/obj/") ||
    filename.includes("/.vscode/") ||
    filename.includes("/.idea/") ||
    filename.includes("/vendor/") ||
    filename.includes("/__pycache__/") ||
    filename.includes("/.pytest_cache/") ||
    filename.includes("/venv/") ||
    filename.includes("/env/")
  ) {
    return true;
  }

  // Drawio specific directories (same as backend)
  if (
    filename.includes("drawio/src/main/webapp/node_modules") ||
    filename.includes("drawio/src/main/webapp/assets") ||
    filename.includes("drawio/src/main/webapp/resources") ||
    filename.includes("drawio/src/main/webapp/shapes") ||
    filename.includes("drawio/src/main/webapp/math") ||
    filename.includes("drawio/src/main/webapp/plugins") ||
    filename.includes("drawio/src/main/webapp/templates") ||
    filename.includes("drawio/src/main/webapp/stencils") ||
    filename.includes("drawio/src/main/webapp/WEB-INF") ||
    filename.includes("drawio/src/main/webapp/images")
  ) {
    return true;
  }

  return false;
};

// Helper function to process files in batches to avoid stack overflow
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const processBatch = async (
  batch: Array<[string, any]>,
  repoData: Record<string, ArchiveFile>
): Promise<number> => {
  let processedInBatch = 0;

  for (const [relativePath, zipEntry] of batch) {
    try {
      // Remove the root folder name from the path (GitHub archives include repo name as root folder)
      const cleanPath = relativePath.replace(/^[^\/]+\//, "");

      // Skip if empty path (was the root folder itself)
      if (!cleanPath) {
        processedInBatch++;
        continue;
      }

      // Apply the same filtering as backend
      if (!isTextFile(cleanPath) || isIgnoreFile(cleanPath)) {
        processedInBatch++;
        continue;
      }

      // Extract file content as text
      const content = await zipEntry.async("text");

      // Store in the same format as backend
      repoData[cleanPath] = {
        path: cleanPath,
        content: content,
        type: "blob",
        fileName: cleanPath.split("/").pop() || "",
      };

      processedInBatch++;
    } catch (error) {
      console.warn(
        `[ZipProcessor] Failed to extract file ${relativePath}:`,
        error
      );
      processedInBatch++;
    }
  }

  return processedInBatch;
};

export const processZipArchive = async (
  base64Data: string,
  onProgress?: (current: number, total: number) => void
): Promise<ProcessedArchive> => {
  console.debug(`[ZipProcessor] Starting to process zip archive`);

  try {
    // Convert base64 to ArrayBuffer
    const binaryString = atob(base64Data);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const arrayBuffer = bytes.buffer;

    const archiveSizeMB = arrayBuffer.byteLength / 1024 / 1024;
    console.debug(`[ZipProcessor] Archive size: ${archiveSizeMB.toFixed(2)}MB`);

    // Load the zip file
    const zip = await JSZip.loadAsync(arrayBuffer);
    const repoData: Record<string, ArchiveFile> = {};

    // Get all files from zip
    const allFiles = Object.entries(zip.files).filter(
      ([_, zipEntry]) => !zipEntry.dir
    );
    const totalFiles = allFiles.length;
    let processedFiles = 0;

    console.debug(`[ZipProcessor] Processing ${totalFiles} files from archive`);

    // For large repositories, use batched processing to avoid stack overflow
    const BATCH_SIZE = Math.min(
      50,
      Math.max(10, Math.floor(1000 / Math.sqrt(totalFiles)))
    );
    const isLargeRepo = totalFiles > 500 || archiveSizeMB > 10;

    if (isLargeRepo) {
      console.debug(
        `[ZipProcessor] Large repository detected (${totalFiles} files, ${archiveSizeMB.toFixed(
          2
        )}MB), using batched processing with batch size: ${BATCH_SIZE}`
      );
    }

    // Process files in batches
    for (let i = 0; i < allFiles.length; i += BATCH_SIZE) {
      const batch = allFiles.slice(i, i + BATCH_SIZE);
      const processedInBatch = await processBatch(batch, repoData);

      processedFiles += processedInBatch;
      onProgress?.(processedFiles, totalFiles);

      // For large repositories, add a small delay between batches to prevent blocking the UI
      if (isLargeRepo && i + BATCH_SIZE < allFiles.length) {
        await delay(1); // Very small delay to let the event loop breathe
      }
    }

    const successfulFiles = Object.keys(repoData).length;
    console.debug(
      `[ZipProcessor] Successfully processed ${successfulFiles}/${totalFiles} files`
    );

    if (successfulFiles === 0 && totalFiles > 0) {
      throw new Error(
        "No valid files found in the repository archive. All files may have been filtered out."
      );
    }

    return { repoData, totalFiles: successfulFiles };
  } catch (error) {
    console.error(`[ZipProcessor] Error processing zip archive:`, error);

    // Provide specific error messages for different scenarios
    if (error instanceof Error) {
      if (error.message.includes("Invalid or unsupported zip format")) {
        throw new Error(
          "The downloaded file is not a valid zip archive. Please try again or use Local mode."
        );
      }

      if (error.message.includes("No valid files found")) {
        throw error; // Re-throw our custom error message
      }
    }

    throw new Error(
      `Failed to process zip archive: ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }
};
